import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Search, PlusCircle, MinusCircle, Loader } from 'lucide-react';
import { formatErrorDetail } from '../utils/errorFormat';

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.surface};
  flex: 1;
  min-width: 220px;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 1px;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: ${props => props.variant === 'secondary'
    ? props.theme.colors.background
    : props.theme.colors.primary};
  color: ${props => props.variant === 'secondary'
    ? props.theme.colors.text
    : '#fff'};
  box-shadow: ${props => props.theme.shadows.sm};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

const StatCard = styled.div`
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const StatTitle = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.textSecondary};
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const MutationsRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const Note = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px dashed ${props => props.theme.colors.border};
`;

function BillingBalances() {
  const queryClient = useQueryClient();
  const [searchId, setSearchId] = useState('');
  const [requestedId, setRequestedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const formatCredits = (value) =>
    Number(value ?? 0).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const { data, isFetching, isError, error } = useQuery(
    ['admin.billing.balance', requestedId],
    async () => {
      if (!requestedId) return null;
      const response = await adminAPI.getUserBalanceAdmin(requestedId);
      return response.data;
    },
    {
      enabled: Boolean(requestedId),
      retry: false,
    }
  );

  const creditMutation = useMutation(
    ({ userId, amount, reason }) =>
      adminAPI.creditUserBalance(userId, amount, reason),
    {
      onSuccess: (_result, variables) => {
        toast.success(`Начислено ${variables.amount} кредитов`);
        queryClient.invalidateQueries(['admin.billing.balance', variables.userId]);
        setAmount('');
        setReason('');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Ошибка начисления');
      },
    }
  );

  const debitMutation = useMutation(
    ({ userId, amount, reason }) =>
      adminAPI.debitUserBalance(userId, amount, reason),
    {
      onSuccess: (_result, variables) => {
        toast.success(`Списано ${variables.amount} кредитов`);
        queryClient.invalidateQueries(['admin.billing.balance', variables.userId]);
        setAmount('');
        setReason('');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Ошибка списания');
      },
    }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchId) {
      toast.error('Укажите TG ID или внутренний ID пользователя');
      return;
    }
    setRequestedId(searchId.trim());
  };

  const handleCredit = (isDebit) => {
    if (!requestedId) {
      toast.error('Сначала выберите пользователя');
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error('Введите сумму > 0');
      return;
    }
    const payload = {
      userId: requestedId,
      amount: numericAmount,
      reason: reason || (isDebit ? 'Manual debit' : 'Manual credit'),
    };
    if (isDebit) {
      debitMutation.mutate(payload);
    } else {
      creditMutation.mutate(payload);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Балансы пользователей</Title>
        <Subtitle>
          Ручное начисление или списание кредитов, просмотр текущего состояния счета. Можно
          использовать TG ID (из Telegram) или внутренний ID пользователя.
        </Subtitle>
      </Header>

      <Card as="form" onSubmit={handleSearch}>
        <FormRow>
          <Label>Пользователь</Label>
          <InputGroup>
            <Input
              type="text"
              placeholder="Введите tg_id или внутренний ID"
              value={searchId}
              onChange={e => setSearchId(e.target.value)}
            />
            <Button type="submit">
              <Search size={16} />
              Найти
            </Button>
          </InputGroup>
        </FormRow>
        <Note>
          Поддерживаются оба формата ID. TG ID — числовой идентификатор из Telegram (обычно
          длинный), внутренний ID — номер пользователя в базе.
        </Note>
      </Card>

      {isFetching && (
        <EmptyState>
          <Loader size={18} /> Загрузка баланса...
        </EmptyState>
      )}

      {isError && (
        <EmptyState>
          {error?.response?.status === 404
            ? 'Пользователь не найден'
            : formatErrorDetail(error, 'Ошибка загрузки данных')}
        </EmptyState>
      )}

      {data && (
        <>
          <Card>
            <FormRow>
              <Label>Данные пользователя</Label>
              <StatsGrid>
                <StatCard>
                  <StatTitle>Внутренний ID</StatTitle>
                  <StatValue>{data.user.internal_id}</StatValue>
                </StatCard>
                <StatCard>
                  <StatTitle>Telegram ID</StatTitle>
                  <StatValue>{data.user.tg_id || '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatTitle>Username</StatTitle>
                  <StatValue>{data.user.username || '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatTitle>Доступно кредитов</StatTitle>
                  <StatValue>{formatCredits(data.balance.credits_available)}</StatValue>
                </StatCard>
                <StatCard>
                  <StatTitle>Всего начислено</StatTitle>
                  <StatValue>{formatCredits(data.balance.credits_total)}</StatValue>
                </StatCard>
                <StatCard>
                  <StatTitle>Обновлено</StatTitle>
                  <StatValue>
                    {data.balance.updated_at
                      ? new Date(data.balance.updated_at).toLocaleString('ru-RU')
                      : '—'}
                  </StatValue>
                </StatCard>
              </StatsGrid>
            </FormRow>
          </Card>

          <Card>
            <FormRow>
              <Label>Операция</Label>
              <InputGroup>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Сумма в кредитах"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
                <Input
                  type="text"
                  placeholder="Комментарий (опционально)"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
              </InputGroup>
              <MutationsRow>
                <Button
                  type="button"
                  onClick={() => handleCredit(false)}
                  disabled={creditMutation.isLoading}
                >
                  <PlusCircle size={16} />
                  Начислить
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleCredit(true)}
                  disabled={debitMutation.isLoading}
                >
                  <MinusCircle size={16} />
                  Списать
                </Button>
              </MutationsRow>
              <Note>
                Обе операции фиксируются в логах и влияют на WebApp сразу после обновления.
                Убедитесь, что вы указываете корректную сумму и комментарий — это поможет
                отследить причину изменения баланса.
              </Note>
            </FormRow>
          </Card>
        </>
      )}
    </Container>
  );
}

export default BillingBalances;

